const MS_DAY = 24 * 60 * 60 * 1000;

export const UNASSIGNED_ASSIGNEE_ID = 0;
export const NO_CLIENT_ID = 0;

export const DUE_FILTERS = {
  OVERDUE: "overdue",
  TODAY: "today",
  WEEK: "week",
  NONE: "none",
};

export const URGENCY_FILTERS = {
  REQUIRED: "required",
  NORMAL: "normal",
};

export const SORT_MODES = {
  BOARD: "board",
  DUE: "due",
};

function startOfLocalDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function parseDueDate(dueAt) {
  if (!dueAt) return null;
  const d = new Date(dueAt);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function matchesDueFilter(card, dueFilter, { columnIsDone = false } = {}) {
  if (!dueFilter) return true;
  if (columnIsDone) {
    return dueFilter === DUE_FILTERS.NONE ? !card?.dueAt : false;
  }
  const due = parseDueDate(card?.dueAt);
  const now = new Date();
  const todayStart = startOfLocalDay(now);
  const tomorrowStart = new Date(todayStart.getTime() + MS_DAY);
  const weekEnd = new Date(todayStart.getTime() + 8 * MS_DAY);

  if (dueFilter === DUE_FILTERS.NONE) return !due;
  if (!due) return false;
  if (dueFilter === DUE_FILTERS.OVERDUE) return due < now;
  if (dueFilter === DUE_FILTERS.TODAY) return due >= todayStart && due < tomorrowStart;
  if (dueFilter === DUE_FILTERS.WEEK) return due >= now && due < weekEnd;
  return true;
}

export function matchesAssigneeFilter(card, assigneeId) {
  if (assigneeId == null) return true;
  const assignees = (card?.assignees || []).map(Number);
  if (Number(assigneeId) === UNASSIGNED_ASSIGNEE_ID) return assignees.length === 0;
  return assignees.includes(Number(assigneeId));
}

export function matchesUrgencyFilter(card, urgency) {
  if (!urgency) return true;
  const required = Boolean(card?.isRequired);
  if (urgency === URGENCY_FILTERS.REQUIRED) return required;
  if (urgency === URGENCY_FILTERS.NORMAL) return !required;
  return true;
}

export function matchesClientFilter(card, clientId) {
  if (clientId == null) return true;
  const resolvedClientId =
    card?.clientId != null && Number(card.clientId) > 0 ? Number(card.clientId) : null;
  if (Number(clientId) === NO_CLIENT_ID) return resolvedClientId == null;
  return resolvedClientId === Number(clientId);
}

export function cardMatchesBoardFilters(card, filters = {}, { columnIsDone = false } = {}) {
  return (
    matchesAssigneeFilter(card, filters.assigneeId) &&
    matchesDueFilter(card, filters.due, { columnIsDone }) &&
    matchesUrgencyFilter(card, filters.urgency) &&
    matchesClientFilter(card, filters.clientId)
  );
}

export function compareCardsByDue(a, b) {
  const aDue = parseDueDate(a?.dueAt);
  const bDue = parseDueDate(b?.dueAt);
  if (!aDue && !bDue) return Number(a?.id || 0) - Number(b?.id || 0);
  if (!aDue) return 1;
  if (!bDue) return -1;
  if (aDue.getTime() !== bDue.getTime()) return aDue.getTime() - bDue.getTime();
  const aReq = Boolean(a?.isRequired) ? 0 : 1;
  const bReq = Boolean(b?.isRequired) ? 0 : 1;
  if (aReq !== bReq) return aReq - bReq;
  return Number(a?.id || 0) - Number(b?.id || 0);
}

export function applyBoardCardFilters(columns = [], filters = {}) {
  const sortByDue = filters.sort === SORT_MODES.DUE;

  return (columns || []).map((col) => {
    let cards = (col.cards || []).filter((card) =>
      cardMatchesBoardFilters(card, filters, {
        columnIsDone: Boolean(col.isDone),
      })
    );
    if (sortByDue) {
      cards = [...cards].sort(compareCardsByDue);
    }
    return { ...col, cards };
  });
}

export function collectBoardClientOptions(columns = [], board = null) {
  const byId = new Map();
  const boardClientId =
    board?.clientId != null && Number(board.clientId) > 0 ? Number(board.clientId) : null;
  if (boardClientId && board?.clientName) {
    byId.set(boardClientId, {
      id: boardClientId,
      name: String(board.clientName).trim() || `Cliente #${boardClientId}`,
    });
  }
  for (const col of columns || []) {
    for (const card of col.cards || []) {
      const id = card?.clientId != null && Number(card.clientId) > 0 ? Number(card.clientId) : null;
      if (!id) continue;
      if (byId.has(id)) continue;
      byId.set(id, {
        id,
        name: String(card.clientName || "").trim() || `Cliente #${id}`,
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

export function collectBoardAssigneeOptions(columns = [], members = [], users = []) {
  const userMap = new Map();
  (users || []).forEach((u) => userMap.set(Number(u.id), u));
  (members || []).forEach((m) => {
    const id = Number(m.userId ?? m.id);
    if (!Number.isFinite(id) || id <= 0) return;
    if (userMap.has(id)) return;
    userMap.set(id, {
      id,
      nome: m.userName || m.nome || m.name || `Usuário #${id}`,
      avatarUrl: m.userAvatarUrl,
    });
  });

  const seen = new Set();
  for (const col of columns || []) {
    for (const card of col.cards || []) {
      for (const id of card.assignees || []) {
        const n = Number(id);
        if (Number.isFinite(n) && n > 0) seen.add(n);
      }
    }
  }
  (members || []).forEach((m) => {
    const id = Number(m.userId ?? m.id);
    if (Number.isFinite(id) && id > 0) seen.add(id);
  });

  return [...seen]
    .map((id) => userMap.get(id) || { id, nome: `Usuário #${id}` })
    .sort((a, b) =>
      String(a.nome || a.name || "").localeCompare(String(b.nome || b.name || ""), "pt-BR")
    );
}

const ASSIGNABLE_BOARD_ROLES = new Set(["owner", "admin", "member"]);

export function formatDeletedUserName(name, deleted, deletedLabel = "excluído") {
  const base = String(name || "").trim();
  if (!base) return "";
  return deleted ? `${base} (${deletedLabel})` : base;
}

/**
 * Options for the task assignee picker.
 * When the board has members, only owner, admin and member can be chosen.
 * Viewers stay out. An empty member list falls back to the workspace user list.
 * `includeIds` keeps already-assigned users visible even if they left the board.
 */
export function buildBoardAssigneePickerOptions(members = [], users = [], { includeIds = [] } = {}) {
  const userMap = new Map();
  for (const u of users || []) {
    const id = Number(u?.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    userMap.set(id, {
      id,
      nome: u.nome || u.name || `Usuário #${id}`,
      avatarUrl: u.avatarUrl || u.imagem || null,
    });
  }

  const memberOptions = [];
  const memberIds = new Set();
  for (const m of members || []) {
    const id = Number(m.userId ?? m.participantId ?? m.id);
    if (!Number.isFinite(id) || id <= 0 || memberIds.has(id)) continue;
    if (m.role && !ASSIGNABLE_BOARD_ROLES.has(m.role)) continue;
    memberIds.add(id);
    const fromUsers = userMap.get(id);
    memberOptions.push({
      id,
      nome:
        fromUsers?.nome ||
        m.userName ||
        m.nome ||
        m.name ||
        `Usuário #${id}`,
      avatarUrl: fromUsers?.avatarUrl || m.userAvatarUrl || null,
    });
  }

  const base =
    (members || []).length > 0
      ? memberOptions
      : Array.from(userMap.values()).sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
        );

  const byId = new Map(base.map((u) => [Number(u.id), u]));
  for (const rawId of includeIds || []) {
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0 || byId.has(id)) continue;
    const fromUsers = userMap.get(id);
    byId.set(id, {
      id,
      nome: fromUsers?.nome || `Usuário #${id}`,
      avatarUrl: fromUsers?.avatarUrl || null,
    });
  }

  return Array.from(byId.values()).sort((a, b) =>
    String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
  );
}

export function hasActiveBoardFilters(filters = {}) {
  return (
    filters.assigneeId != null ||
    Boolean(filters.due) ||
    Boolean(filters.urgency) ||
    filters.clientId != null ||
    filters.sort === SORT_MODES.DUE
  );
}
